import React from 'react';
import { Controller } from 'react-hook-form';

const DynamicForm = ({ fields, data, onSubmit, isLoading, control: externalControl, onCancel, errors, isReadOnly, departments,uoms,countries, regions, cities }) => {

  const getFieldValue = (fieldName) => {
    let value;

    if (isReadOnly) {
        if (fieldName === 'buy_price' && data?.prices?.buy_price_new !== undefined) {
            value = data?.prices?.buy_price_new;
        } else if (fieldName === 'sell_price' && data?.prices?.sell_price_new !== undefined) {
            value = data?.prices?.sell_price_new;
        } else if (fieldName === 'prices_buy_uom_id' && data?.prices?.buy_uom_name !== undefined) {
            value = data?.prices?.buy_uom_name;
        } else if (fieldName === 'prices_sell_uom_id' && data?.prices?.sell_uom_name !== undefined) {
            value = data?.prices?.sell_price_new;
        } else if (fieldName === 'country_id' && data?.countries?.name !== undefined) {
            value = data?.countries?.name;
        } else if (fieldName === 'region_id' && data?.regions?.name !== undefined) {
          value = data?.regions?.name;
        } else if (fieldName === 'district_id' && data?.districts?.name !== undefined) {
          value = data?.districts?.name;
        } else {
            value = data?.[fieldName];
        }
    } else {
        value = data?.[fieldName];
    }

    if (value === null || value === undefined || value === '') {
      return 'Not specified';
    }

    const fieldConfig = fields.find(f => f.name === fieldName);

    if (fieldConfig?.type === 'date') {
        try {
            const date = new Date(value);
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        } catch (e) {
            return 'Invalid Date';
        }
    }
    
    if (fieldName === 'department_id' && departments) {
      const selectedDepartment = departments.find(dept => dept.value === value);
      return selectedDepartment?.label || value;
    }

    if (fieldName === 'country' && countries) {
      const selectedOption = countries.find(option => String(option.value) === String(value));
      return selectedOption?.label || value;
    }
    if (fieldName === 'region' && regions) {
      const selectedOption = regions.find(option => String(option.value) === String(value));
      return selectedOption?.label || value;
    }
    if (fieldName === 'city' && cities) {
      const selectedOption = cities.find(option => String(option.value) === String(value));
      return selectedOption?.label || value;
    }

    if (fieldConfig?.type === 'select') {
      const selectedOption = fieldConfig.options?.find(option => String(option.value) === String(value));
      return selectedOption?.label || value;
    }

    if (fieldConfig?.type === 'checkbox') {
        if (fieldConfig.toggleType) {
            return value ? fieldConfig.toggleType.onLabel : fieldConfig.toggleType.offLabel;
        }
      return value ? 'Yes' : 'No';
    }

    return value;
  };

  const renderInput = (field) => {
    if (isReadOnly) {
      return (
        // Increased font size for read-only display
        <div className="bg-gray-500 text-white rounded-md p-2 text-lg">
          {getFieldValue(field.name)}
        </div>
      );
    }

    switch (field.type) {
      case 'select': {
        const hasSelectError = errors && errors[field.name];

        let selectOptions = field.options || [];
        
        if (field.name === 'department_id' && Array.isArray(departments)) {
          selectOptions = departments.map((dept) => ({
            value: String(dept.value),
            label: dept.label,
          }));
        } else if ((field.name.includes('uom_name') || field.name.includes('uom_id')) && Array.isArray(uoms)) {
          selectOptions = uoms.map((uom) => ({
            value: String(uom.value),
            label: uom.label,
          }));
        } else if (field.name === 'country' && Array.isArray(countries)) {
          selectOptions = countries.map(country => ({
            value: String(country.value),
            label: country.label,
          }));
        } else if (field.name === 'region' && Array.isArray(regions)) {
          selectOptions = regions.map(region => ({
            value: String(region.value),
            label: region.label,
          }));
        } else if (field.name === 'city' && Array.isArray(cities)) {
          selectOptions = cities.map(city => ({
            value: String(city.value),
            label: city.label,
          }));
        }

        return (
          <Controller
            name={field.name}
            control={externalControl}
            rules={field.rules}
            render={({ field: controllerField }) => {
              const value = controllerField.value != null ? String(controllerField.value) : '';

              return (
                <div className="relative w-full">
                  <select
                    {...controllerField}
                    value={value}
                    id={field.name}
                    // Increased font size for select input
                    className="w-full bg-gray-500 text-white border-b-2 focus:outline-none focus:border-cyan-300 border-cyan-500 text-lg"
                  >
                     <option value="" className="text-gray-800">
                      {field.placeholder || `Select ${field.label}`}
                    </option>
                    {selectOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {hasSelectError && (
                    // Increased font size for error message
                    <p className="text-red-500 text-sm mt-1">{hasSelectError.message}</p>
                  )}
                </div>
              );
            }}
          />
        );
      }

      case 'checkbox':
        if (field.toggleType) {
           return (
              <Controller
                name={field.name}
                control={externalControl}
                rules={field.rules}
                render={({ field: controllerField }) => (
                  <div className="flex items-center justify-between w-full">
                    {/* Increased font size for toggle labels */}
                    <span className={`text-base ${controllerField.value ? 'text-gray-400' : 'text-cyan-300'}`}>
                      {field.toggleType.offLabel}
                    </span>
                    <label htmlFor={field.name} className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        id={field.name}
                        {...controllerField}
                        checked={controllerField.value}
                        className="sr-only peer"
                      />
                      <span className="w-11 h-6 bg-gray-500 rounded-full peer-checked:bg-cyan-500 transition-colors duration-200 ease-in-out"></span>
                      <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 ease-in-out peer-checked:translate-x-5"></span>
                    </label>
                    {/* Increased font size for toggle labels */}
                    <span className={`text-base ${controllerField.value ? 'text-cyan-300' : 'text-gray-400'}`}>
                      {field.toggleType.onLabel}
                    </span>
                  </div>
                )}
              />
            );
        } else {
           return (
            <Controller
              name={field.name}
              control={externalControl}
              rules={field.rules}
              render={({ field: controllerField }) => (
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id={field.name}
                    {...controllerField}
                    checked={controllerField.value}
                    className="peer hidden"
                  />
                  <label
                    htmlFor={field.name}
                    className="w-5 h-5 flex items-center justify-center border-2 border-cyan-500 rounded-md transition-all duration-300 cursor-pointer peer-checked:bg-cyan-500 peer-checked:shadow-[0_0_10px_2px_rgba(6,182,212,0.8)]"
                  >
                    <svg
                      className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </label>
                  {/* Increased font size for checkbox label */}
                  <span className="text-white text-base">{field.label}</span>
                </div>
              )}
            />
          );
        }

      case 'date':
        const hasDateError = errors && errors[field.name];
        return (
          <Controller
            name={field.name}
            control={externalControl}
            rules={field.rules}
            render={({ field: { onChange, value, ref } }) => (
              <>
                <input
                  type="date"
                  value={value}
                  onChange={onChange}
                  // Increased font size for date input
                  className={`w-full bg-transparent border-b-2 text-white placeholder:text-white focus:outline-none focus:border-cyan-300 border-cyan-500 text-lg`}
                  ref={ref}
                />
                {hasDateError && (
                  // Increased font size for error message
                  <p className="text-red-500 text-sm mt-1">{hasDateError.message}</p>
                )}
              </>
            )}
          />
        );

      case 'textarea':
        const hasTextareaError = errors && errors[field.name];
        return (
          <Controller
            name={field.name}
            control={externalControl}
            rules={field.rules}
            render={({ field }) => (
              <>
                <textarea
                  {...field}
                  id={field.name}
                  rows={4}
                  // Increased font size for textarea
                  className={`peer w-full bg-transparent border-b-2 text-white placeholder-transparent focus:outline-none focus:border-cyan-300 mt-0 mb-0 
                border-cyan-500 text-lg
             `}
                  placeholder={field.placeholder || field.label}
                />
                {hasTextareaError && (
                  // Increased font size for error message
                  <p className="text-red-500 text-sm mt-1">{hasTextareaError.message}</p>
                )}
              </>
            )}
          />
        );

      default:
        const hasError = errors && errors[field.name];
        return (
          <Controller
            name={field.name}
            control={externalControl}
            rules={field.rules}
            render={({ field }) => (
              <>
                <input
                  {...field}
                  id={field.name}
                  type={field.type}
                  placeholder={field.placeholder || field.label}
                  // Increased font size for default input
                  className={`w-full bg-transparent border-b-2 text-white placeholder-transparent focus:outline-none focus:border-cyan-300 mt-0 mb-0 border-cyan-500 text-lg`
                  }
                />
                {hasError && (
                  // Increased font size for error message
                  <p className="text-red-500 text-sm mt-1">{hasError.message}</p>
                )}
              </>
            )}
          />
        );
    }
  };

  // Function to log errors for debugging when the submit button is clicked
  const handleDiagnosticClick = () => {
    console.log('--- SUBMIT BUTTON CLICKED (DynamicForm) ---');
    if (errors && Object.keys(errors).length > 0) {
        console.error('Submission Blocked by React Hook Form Validation Errors:', errors);
        console.log('Please fill in all required fields indicated by error messages.');
    } else {
        console.log('No RHF errors detected. Submission should now proceed to handleCategoryFormSubmit in NewCategory.jsx.');
    }
    console.log('-------------------------------------------');
  };

  return (
    <React.Fragment>
      <div className="space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-fit mx-auto">
          {fields?.map((field) => (
            <div key={field.name} className="relative">
              {field.type !== 'checkbox' ? (
                <div className="flex flex-col gap-[1px]">
                  {/* Increased font size for main labels */}
                  <label htmlFor={field.name} className="text-base text-cyan-300 text-left">
                    {field.label}
                  </label>
                  {renderInput(field)}
                </div>
              ) : (
                renderInput(field)
              )}
            </div>
          ))}
        </div>

        {!isReadOnly && (
          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white font-semibold shadow-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              onClick={handleDiagnosticClick} // <-- Diagnostic logging added here
              className="px-6 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white font-semibold shadow-md"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default DynamicForm;
